import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

const CONTACT_API = '/php/api/contact.api.php';

/** Quanto il server pretende che passi tra il token e l'invio (contact.api.php). */
const MIN_AGE_MS = 3100;

export interface ContactPayload {
  name: string;
  message: string;
  email?: string;
  phone?: string;
  source?: string;
}

/**
 * Invio del form contatti. Lo usa shared/contact-form, che è il form di tutte
 * le pagine: chi aggiunge un form nuovo usa quel componente, non questo.
 *
 * Il server rilascia un token firmato con l'ora di emissione e rifiuta gli
 * invii arrivati troppo presto o con un token falso: prima l'ora la mandava il
 * browser, e un bot che scrive le chiamate a mano metteva un orario vecchio e
 * passava. Il token si chiede appena la pagina è nel browser (mai durante il
 * prerender), così chi compila il form non aspetta nulla; se all'invio non c'è
 * ancora, lo si chiede e si attende il minimo prima di spedire.
 */
@Injectable({ providedIn: 'root' })
export class ContactService {

  private http    = inject(HttpClient);
  private browser = isPlatformBrowser(inject(PLATFORM_ID));
  private token: string | null = null;

  prime(): void {
    if (!this.browser || this.token) return;
    this.fetchToken().subscribe({ error: () => {} });
  }

  send(payload: ContactPayload): Observable<{ ok: boolean }> {
    const body = (t: string) => ({ ...payload, website: '', _t: t });
    const post = (t: string) => this.http.post<{ ok: boolean }>(CONTACT_API, body(t)).pipe(
      tap(() => { this.token = null; this.prime(); }),
    );

    if (this.token) return post(this.token);
    return this.fetchToken().pipe(
      switchMap(t => timer(MIN_AGE_MS).pipe(map(() => t))),
      switchMap(post),
    );
  }

  private fetchToken(): Observable<string> {
    return this.http.get<{ t: string }>(CONTACT_API + '?token=1').pipe(
      map(r => r.t),
      tap(t => this.token = t),
    );
  }
}
